import React, { useEffect, useRef, useState } from "react";
import { Collapse, Form, ListGroup, Table } from "react-bootstrap";
import { CiMenuKebab } from "react-icons/ci";
import { useParams } from "react-router";
import { FaEdit, FaTrash, FaTrashRestore } from "react-icons/fa";
import { cdiscManagementService } from "../../../APIServices/CDISC/cdiscManagementService";
import { NoDataFoundTable, PageLoader } from "../../../Components/NoDataFoundTable/NoDataFoundTable";
import Swal from "sweetalert2";
import SplitPane from "react-split-pane";
import Pagination from "../../Pagination";
import AddFormLibrary from "./AddFormLibrary";
import UseToggle from "../../../Hooks/UseToggle";
import UpdateFormLibrary from "./UpdateFormLibrary";
import UpdateFormTemplate from "./UpdateFormTemplate";
import PullLibraryMetadata from "./PullLibraryMetadata";
import DeletedLibraryMetadata from "./DeletedLibraryMetadata";
import AddFormLibraryTemplate from "./AddFormLibraryTemplate";
import DeletedTemplateMetadata from "./DeletedTemplateMetadata";

const FormLibraryManagement = ({ onMouseDown, tableRef }) => {
  const toggles = UseToggle();
  const { studyId } = useParams();
  const [loading, setLoading] = useState(false);
  const [libraryList, setFormLibraryList] = useState([]);
  const [libraryData, setLibraryData] = useState(null);
  const [libraryId, setLibraryId] = useState("");
  const [formId, setFormId] = useState("");

  const [libraryTemplate, setFormLibraryTemplate] = useState([]);
  const [libraryTemplateCount, setFormLibraryTemplateCount] = useState(0);
  const [templateData, setTemplateData] = useState(null);
  const [loader, setLoader] = useState(false);

  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const startIndex = (pageNumber - 1) * itemsPerPage;
  const totalPage = Math.ceil(libraryTemplateCount / itemsPerPage);

  const getStudyFormLibraryData = async () => {
    try {
      setLoading(true);
      const data = await cdiscManagementService.getStudyFormLibraryData(
        "active",
        studyId
      );
      setFormLibraryList(data?.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studyId) {
      getStudyFormLibraryData();
    }
  }, [studyId]);

  const [pageNumberDeleted, setPageNumberDeleted] = useState(1);
  const [itemsPerPageDeleted, setItemsPerPageDeleted] = useState(10);
  const [deletedLibrary, setDeletedLibrary] = useState([]);
  const [deletedLibraryCount, setDeletedLibraryCount] = useState(0);

  const getDeletedLibrary = async (pageNumberDeleted, itemsPerPageDeleted) => {
    let data = await cdiscManagementService.getStudyFormLibraryData(
      "deleted",
      studyId,
      "",
      pageNumberDeleted,
      itemsPerPageDeleted
    );
    setDeletedLibrary(data?.data || []);
    setDeletedLibraryCount(data?.count || 0);
  };

  const softDeleteRestoreStudyFormLibrary = async (id, type) => {
    Swal.fire({
      title: "Are you sure?",
      text: `You want to ${type} library!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#103C5E",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${type} it!`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        let userData = {};
        userData.id = id;
        userData.type = type;
        userData.studyId = studyId;
        let data =
          await cdiscManagementService.softDeleteRestoreStudyFormLibrary(
            userData
          );
        if (data?.statusCode === 200) {
          getStudyFormLibraryData();
          getDeletedLibrary();
          if (type === "restore") {
            getDeletedLibrary(pageNumberDeleted, itemsPerPageDeleted);
            toggles.setToggle5();
          }
        }
      }
    });
  };

  const getStudyFormTemplatesData = async () => {
    try {
      setLoader(true);
      const data = await cdiscManagementService.getStudyFormTemplatesData(
        "active",
        studyId,
        formId,
        "",
        pageNumber,
        itemsPerPage
      );
      setFormLibraryTemplate(data?.data || []);
      setFormLibraryTemplateCount(data?.count || 0);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (formId && studyId) {
      getStudyFormTemplatesData();
    }
  }, [studyId, formId, pageNumber, itemsPerPage]);

  const updateLibrary = (item) => {
    setLibraryId(item?.formId);
    setLibraryData(item);
    toggles.setToggle1();
  };

  const [deletedTemplate, setDeletedTemplate] = useState([]);
  const [deletedTemplateCount, setDeletedTemplateCount] = useState(0);

  const getDeletedTemplate = async (pageNumberDeleted, itemsPerPageDeleted) => {
    let data = await cdiscManagementService.getStudyFormTemplatesData(
      "deleted",
      studyId,
      libraryId,
      "",
      pageNumberDeleted,
      itemsPerPageDeleted
    );
    setDeletedTemplate(data?.data || []);
    setDeletedTemplateCount(data?.count || 0);
  };

  const softDeleteRestoreStudyFormTemplates = async (id, type) => {
    Swal.fire({
      title: "Are you sure?",
      text: `You want to ${type} template!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#103C5E",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${type} it!`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        let userData = {};
        userData.id = id;
        userData.type = type;
        userData.studyId = studyId;
        let data =
          await cdiscManagementService.softDeleteRestoreStudyFormTemplates(
            userData
          );
        if (data?.statusCode === 200) {
          getStudyFormTemplatesData();
          getDeletedTemplate();
          if (type === "restore") {
            getDeletedTemplate(pageNumberDeleted, itemsPerPageDeleted);
            toggles.setToggle4();
          }
        }
      }
    });
  };

  const AppsRef = useRef(null);
  const [openOption, setOpenOption] = useState(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (AppsRef.current && !AppsRef.current.contains(event.target)) {
        setOpenOption(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  return (
    <>
      <SplitPane
        split="vertical"
        minSize={"20%"}
        className="position-relative"
        style={{ height: "calc(100vh - 145px)" }}
      >
        <>
          <div
            className="hstack justify-content-between border-bottom py-1 px-2 toolbar"
            style={{ height: 32 }}
          >
            <Form.Label className="m-0 fw-bold">Form Library</Form.Label>
            <div className="hstack gap-2">
              <i
                className="fa-solid fa-code-pull-request"
                title="Pull Form"
                onClick={toggles.setToggle6}
              />
              <div className="vr" />
              <i
                className="fa-solid fa-plus"
                title="Add Form"
                onClick={toggles.setToggle}
              />
              {deletedLibraryCount > 0 && (
                <>
                  <div className="vr" />
                  <i
                    className="fa-solid fa-trash-restore"
                    title="Deleted Form"
                    onClick={toggles.setToggle5}
                  />
                </>
              )}
            </div>
          </div>
          <div
            className="bg-light overflow-auto"
            style={{ height: "calc(100vh - 179px)" }}
          >
            {loading ? (
              <PageLoader />
            ) : libraryList?.length === 0 ? (
              <NoDataFoundTable MSG={"Form library list not found!"} />
            ) : (
              <div className="p-2">
                <ListGroup>
                  {libraryList.map((item, index) => {
                    const isMenuOpen = openOption === index;
                    return (
                      <ListGroup.Item
                        action
                        ref={AppsRef}
                        title={item?.label}
                        onClick={() => setFormId(item?.formId)}
                        key={item?._id || index}
                        className={`hstack justify-content-between p-0 ${
                          formId === item?.formId && "selectedMenu"
                        }`}
                      >
                        <div style={{ padding: "5px 10px 7px 10px" }}>
                          <span>{item?.label} ({item?.name})</span>
                        </div>
                        <div
                          className="position-relative"
                          style={{ padding: "5px 10px 7px 10px" }}
                        >
                          <div
                            onClick={(e) => {
                              setOpenOption(isMenuOpen ? null : index);
                              e.stopPropagation();
                            }}
                          >
                            <CiMenuKebab />
                          </div>
                          <Collapse in={isMenuOpen}>
                            <div
                              className="position-absolute end-0 z-1"
                              style={{ top: 36 }}
                            >
                              <ListGroup className="br3" style={{ fontSize: 12 }}>
                                <ListGroup.Item
                                  onClick={() => {
                                    toggles.setToggle2();
                                    setLibraryId(item?.formId);
                                  }}
                                  action
                                  className="text-nowrap py-1 px-3 text-success"
                                >
                                  <FaEdit className="mb-1 me-1" /> Add Form
                                  Template
                                </ListGroup.Item>
                                <ListGroup.Item
                                  onClick={() => updateLibrary(item)}
                                  action
                                  className="text-nowrap py-1 px-3 text-primary"
                                >
                                  <FaEdit className="mb-1 me-1" /> Edit Library
                                  Name
                                </ListGroup.Item>
                                <ListGroup.Item
                                  onClick={() =>
                                    softDeleteRestoreStudyFormLibrary(
                                      item?._id,
                                      "delete"
                                    )
                                  }
                                  action
                                  className="text-nowrap py-1 px-3 text-danger"
                                >
                                  <FaTrash className="mb-1 me-1" /> Delete Library
                                </ListGroup.Item>
                                <ListGroup.Item
                                  action
                                  className="text-nowrap py-1 px-3"
                                  onClick={(e) => {
                                    toggles.setToggle4();
                                    setLibraryId(item.formId);
                                    e.stopPropagation();
                                  }}
                                >
                                  <FaTrashRestore className="mb-1 me-1" /> Deleted
                                  Form Template
                                </ListGroup.Item>
                              </ListGroup>
                            </div>
                          </Collapse>
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              </div>
            )}
          </div>
        </>

        {loader ? (
          <PageLoader />
        ) : formId ? (
          libraryTemplateCount === 0 ? (
            <NoDataFoundTable MSG={"Library Template data not found!"} />
          ) : (
            <>
              <Table ref={tableRef} className="m-0 custom-table" hover striped>
                <thead className="position-sticky top-0">
                  <tr>
                    <th
                      onMouseDown={(e) => onMouseDown(e, 0)}
                      className="text-center"
                      style={{ width: 40 }}
                    >
                      Sr.
                    </th>
                    <th onMouseDown={(e) => onMouseDown(e, 1)}>Name</th>
                    <th onMouseDown={(e) => onMouseDown(e, 2)}>Label</th>
                    <th onMouseDown={(e) => onMouseDown(e, 3)}>Type</th>
                    <th onMouseDown={(e) => onMouseDown(e, 4)}>Permission</th>
                    <th
                      onMouseDown={(e) => onMouseDown(e, 5)}
                      className="text-center"
                      style={{ width: 60 }}
                    >
                      Edit
                    </th>
                    <th
                      onMouseDown={(e) => onMouseDown(e, 6)}
                      className="text-center"
                      style={{ width: 60 }}
                    >
                      Delete
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {libraryTemplate?.map((item, index) => (
                    <tr key={item._id || index}>
                      <td className="text-center">{startIndex + index + 1}</td>
                      <td>{item.name}</td>
                      <td>{item.label}</td>
                      <td className="text-capitalize">{item.type}</td>
                      <td className="text-capitalize">{item.permission}</td>
                      <td
                        className="text-center CP"
                        title={`Update ${item.name}`}
                        onClick={() => {
                          setTemplateData(item);
                          toggles.setToggle3();
                        }}
                      >
                        <FaEdit className="text-muted" />
                      </td>
                      <td
                        className="text-center CP"
                        title={`Delete ${item.name}`}
                        onClick={() =>
                          softDeleteRestoreStudyFormTemplates(
                            item?._id,
                            "delete"
                          )
                        }
                      >
                        <FaTrash className={"text-danger"} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="position-sticky bottom-0 start-0">
                <Pagination
                  totalPage={totalPage}
                  pageNumber={pageNumber}
                  itemsPerPage={itemsPerPage}
                  totalItems={libraryTemplateCount}
                  setPageNumber={setPageNumber}
                  setItemsPerPage={setItemsPerPage}
                />
              </div>
            </>
          )
        ) : (
          <NoDataFoundTable MSG={"Select any library!"} />
        )}
      </SplitPane>

      {/* Add Library Modal */}
      <AddFormLibrary
        Show={!toggles.toggle}
        Hide={toggles.setToggle}
        studyId={studyId}
        Title="Add Form Library"
        getStudyFormLibraryData={getStudyFormLibraryData}
      />

      {/* Update Library Modal */}
      <UpdateFormLibrary
        Show={!toggles.toggle1}
        Hide={toggles.setToggle1}
        libraryData={libraryData}
        studyId={studyId}
        Title="Update Form Library"
        getStudyFormLibraryData={getStudyFormLibraryData}
      />

      {/* Add Library Template Modal */}
      <AddFormLibraryTemplate
        Show={!toggles.toggle2}
        Hide={toggles.setToggle2}
        libraryData={libraryData}
        libraryId={libraryId}
        studyId={studyId}
        Title="Add Form Library Template"
        getStudyFormTemplatesData={getStudyFormTemplatesData}
      />

      {/* Update Template Modal */}
      <UpdateFormTemplate
        Show={!toggles.toggle3}
        Hide={toggles.setToggle3}
        templateData={templateData}
        studyId={studyId}
        Title="Update Form Template"
        getStudyFormTemplatesData={getStudyFormTemplatesData}
      />

      {/* Deleted Template data Modal */}
      <DeletedTemplateMetadata
        Show={!toggles.toggle4}
        Hide={toggles.setToggle4}
        tableRef={tableRef}
        libraryId={libraryId}
        onMouseDown={onMouseDown}
        deletedTemplate={deletedTemplate}
        getDeletedTemplate={getDeletedTemplate}
        deletedTemplateCount={deletedTemplateCount}
        pageNumberDeleted={pageNumberDeleted}
        itemsPerPageDeleted={itemsPerPageDeleted}
        setPageNumberDeleted={setPageNumberDeleted}
        setItemsPerPageDeleted={setItemsPerPageDeleted}
        softDeleteRestoreStudyFormTemplates={
          softDeleteRestoreStudyFormTemplates
        }
        Title={"Deleted Template History"}
      />

      {/* Deleted Template data Modal */}
      <DeletedLibraryMetadata
        Show={!toggles.toggle5}
        Hide={toggles.setToggle5}
        tableRef={tableRef}
        onMouseDown={onMouseDown}
        deletedLibrary={deletedLibrary}
        getDeletedLibrary={getDeletedLibrary}
        deletedLibraryCount={deletedLibraryCount}
        pageNumberDeleted={pageNumberDeleted}
        itemsPerPageDeleted={itemsPerPageDeleted}
        setPageNumberDeleted={setPageNumberDeleted}
        setItemsPerPageDeleted={setItemsPerPageDeleted}
        softDeleteRestoreStudyFormLibrary={softDeleteRestoreStudyFormLibrary}
        Title={"Deleted Library History"}
      />

      {/* Update Template Modal */}
      <PullLibraryMetadata
        Show={!toggles.toggle6}
        Hide={toggles.setToggle6}
        studyId={studyId}
        tableRef={tableRef}
        onMouseDown={onMouseDown}
        getStudyFormLibraryData={getStudyFormLibraryData}
        Title="Pull Form Library"
      />
    </>
  );
};

export default FormLibraryManagement;
